import React from 'react'
import { useClient } from 'jsonapi-react'
import map from 'lodash/map'
import filter from 'lodash/filter'
import { useHistory, useParams } from 'react-router'

import { Compositions } from 'components/Compositions'
import Input from 'components/Forms/Input'
import { Body } from 'layouts/basic'
import propTypes from 'prop-types'


export const CompositionsContext = React.createContext()


export default function CompositionBaseScene(props) {
  const client = useClient()
  const [hasMore, setHasMore] = React.useState(null)
  const [isFetchingSearch, setIsFetchingSearch] = React.useState(false)
  const history = useHistory()
  const { s } = useParams()

  const page = React.useRef(0)
  const filters = React.useRef(props.filters || {})
  const timeout = React.useRef(null)

  if (s !== '') {
    filters.current = {
      'filter[search]': s,
    }
  }

  React.useEffect(() => {
    dispatch({
      type: 'COMPOSITIONS_RESET',
    })
    page.current = 0
    loadMore()
  }, [s]) // eslint-disable-line

  const [compositions, dispatch] = React.useReducer(
    (state, action) => {
      switch (action.type) {
        case 'COMPOSITION_UPDATE':
          return map(
            state,
            item => {
              if (item.id !== action.item.id) {
                return item
              }
              return {
                ...item,
                ...action.item,
              }
            }
          )
        case 'COMPOSITIONS_GET':
          return [...state, ...action.data]
        case 'COMPOSITIONS_RESET':
          return []
        case 'COMPOSITION_DELETE':
          return filter(state, item => item.id !== action.id)
        default:
          return state
      }
    },
    [],
  )

  const [isFetching, setIsFetching] = React.useState(false)

  const loadMore = async () => {
    if (isFetching) return

    setIsFetching(true)

    page.current = page.current + 1

    const { data, error } = await client.fetch([
      ...props.query,
      {
        include: 'created_by,created_by.profile_pic,exported_file,downloads.file',
        'fields[Composition]': 'title,description,permissions,privacy,created_by,created_at,exported_file,status,downloads',
        'fields[User]': 'full_name,profile_pic',
        'fields[File]': 'title,type,file,thumbnail,medium',
        'page[number]': page.current,
        'sort': '-createdAt',
        ...filters.current,
      }
    ])

    if (!error) {
      dispatch({
        type: 'COMPOSITIONS_GET',
        data,
      })
      setHasMore(true)

    } else {
      setHasMore(false)

    }
    setIsFetching(false)
    setIsFetchingSearch(false)

  }

  React.useEffect(() => {
    loadMore()
  }, []) // eslint-disable-line

  const onChange = async e => {
    setIsFetchingSearch(true)
    const value = e.currentTarget.value

    if (timeout.current)
    clearTimeout(timeout.current)

    timeout.current = setTimeout(() => {
      if (value !== '') {
        history.push(`${props.baseRedirect}/s/${value}`)

      } else {
        history.push(props.baseRedirectEmpty || props.baseRedirect)

      }
    }, 800)

  }

  return (
    <CompositionsContext.Provider value={{ compositions, dispatch }}>
      <div className='p-10'>
        <div className='max-w-2xl mx-auto'>
          <Input
            loading={isFetchingSearch}
            onChange={onChange}
            className='mb-0'
            inputProps={{
              placeholder: 'Search...',
              defaultValue: s,
            }}
          />
        </div>
      </div>
      <Body noPadding loading={!!(props.loading || (isFetching && compositions.length === 0))}>
        <Compositions
          loadMore={loadMore}
          hasMore={hasMore !== false}
          items={compositions}
          loading={isFetching}
        />
      </Body>
    </CompositionsContext.Provider>
  )
}


CompositionBaseScene.propTypes = {
  filters: propTypes.object,
  query: propTypes.array.isRequired,
  baseRedirect: propTypes.string.isRequired,
  baseRedirectEmpty: propTypes.string.isRequired,
  loading: propTypes.bool,
}

CompositionBaseScene.defaultValue = {
  loading: false,
  query: ['compositions'],
  baseRedirect: '/',
  baseRedirectEmpty: '/',
}
